/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
/* eslint-env jest */
/* eslint-disable react/prop-types */
import { act, render, screen, fireEvent } from '@testing-library/react';
import { WorkSetupModal } from './WorkSetupModal';

// WorkSetupModal pulls in GeneralModal/ModalButton via the shared components
// barrel (src/components/index.js), which transitively re-exports an ESM-only
// @react-pdf/renderer that Jest cannot parse. Stub it out. (jest.mock is
// hoisted above imports by babel-jest.)
jest.mock('@react-pdf/renderer', () => ({
  Document: 'Document',
  Page: 'Page',
  Text: 'Text',
  View: 'View',
  Image: 'Image',
  StyleSheet: { create: (styles) => styles },
  pdf: jest.fn(),
}));

// Mock the MUI TimePicker with a plain <input> so the tests drive the modal's
// own conversion/validation logic deterministically. The real masked clock
// input is unreliable in jsdom under fake timers; the picker's internals are
// MUI's concern, not this component's. The mock exposes the label for querying
// and forwards a Date to onChange. Test values use offset-less datetime
// strings so new Date()/format() stay in local time regardless of TZ.
jest.mock('@mui/x-date-pickers/TimePicker', () => ({
  TimePicker: ({ label, value, onChange }) => (
    <input
      aria-label={label}
      value={value ? value.toISOString() : ''}
      onChange={(e) =>
        onChange(e.target.value ? new Date(e.target.value) : null)
      }
    />
  ),
}));

beforeEach(() => {
  jest.useFakeTimers();
  jest.setSystemTime(new Date('2026-07-10T08:00:00'));
});

afterEach(() => {
  jest.runOnlyPendingTimers();
  jest.useRealTimers();
});

test('shows a required lunch time field only when Deployed is selected', () => {
  render(<WorkSetupModal open={true} onConfirm={jest.fn()} />);
  expect(screen.queryByLabelText(/lunch break start/i)).toBeNull();
  fireEvent.click(screen.getByLabelText('Deployed'));
  expect(screen.getByLabelText(/lunch break start/i)).toBeInTheDocument();
});

test('blocks confirm when Deployed has no lunch time', () => {
  const onConfirm = jest.fn();
  render(<WorkSetupModal open={true} onConfirm={onConfirm} />);
  fireEvent.click(screen.getByLabelText('Deployed'));
  fireEvent.click(screen.getByText('Confirm'));

  act(() => {
    jest.advanceTimersByTime(300);
  });

  expect(onConfirm).not.toHaveBeenCalled();
  expect(
    screen.getByText('Please set your lunch break start time.')
  ).toBeInTheDocument();
});

test('confirms with the formatted HH:mm lunch time for a future time', () => {
  const onConfirm = jest.fn();
  render(<WorkSetupModal open={true} onConfirm={onConfirm} />);
  fireEvent.click(screen.getByLabelText('Deployed'));
  fireEvent.change(screen.getByLabelText(/lunch break start/i), {
    target: { value: '2026-07-10T13:30:00' },
  });
  fireEvent.click(screen.getByText('Confirm'));

  act(() => {
    jest.advanceTimersByTime(300);
  });

  expect(onConfirm).toHaveBeenCalledWith('Deployed', '13:30');
});

test('confirms a lunch time earlier in the clock day than now (overnight shift)', () => {
  jest.setSystemTime(new Date('2026-07-10T20:00:00'));
  const onConfirm = jest.fn();
  render(<WorkSetupModal open={true} onConfirm={onConfirm} />);
  fireEvent.click(screen.getByLabelText('Deployed'));
  fireEvent.change(screen.getByLabelText(/lunch break start/i), {
    target: { value: '2026-07-10T00:00:00' },
  });
  fireEvent.click(screen.getByText('Confirm'));

  act(() => {
    jest.advanceTimersByTime(300);
  });

  expect(onConfirm).toHaveBeenCalledWith('Deployed', '00:00');
});

// A time far past check-in is a mistyped entry, not an overnight break. Left
// unchecked it resolves to the next day, placing lunch outside the shift so no
// hour is deducted and the timesheet over-reports.
test('blocks confirm when the lunch time resolves beyond the allowed window', () => {
  jest.setSystemTime(new Date('2026-07-10T07:00:00'));
  const onConfirm = jest.fn();
  render(<WorkSetupModal open={true} onConfirm={onConfirm} />);
  fireEvent.click(screen.getByLabelText('Deployed'));
  fireEvent.change(screen.getByLabelText(/lunch break start/i), {
    target: { value: '2026-07-10T06:00:00' },
  });
  fireEvent.click(screen.getByText('Confirm'));

  act(() => {
    jest.advanceTimersByTime(300);
  });

  expect(onConfirm).not.toHaveBeenCalled();
  expect(
    screen.getByText(
      'Lunch break must start within 8 hours of your check-in time.'
    )
  ).toBeInTheDocument();
});

test('sends empty lunch time when switching away from Deployed before confirming', () => {
  const onConfirm = jest.fn();
  render(<WorkSetupModal open={true} onConfirm={onConfirm} />);
  fireEvent.click(screen.getByLabelText('Deployed'));
  fireEvent.change(screen.getByLabelText(/lunch break start/i), {
    target: { value: '2026-07-10T13:30:00' },
  });
  fireEvent.click(screen.getByLabelText('WFH'));
  fireEvent.click(screen.getByText('Confirm'));

  act(() => {
    jest.advanceTimersByTime(300);
  });

  expect(onConfirm).toHaveBeenCalledWith('WFH', '');
});
